// File: src/components/Invoices/OperationalPOInvoiceChain.jsx

import React from "react";
import {
  Link2,
  Receipt,
  CheckCircle,
  Clock,
  AlertTriangle,
  CreditCard,
  PackageCheck,
  ArrowRight,
} from "lucide-react";

const currency = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

function statusClass(status) {
  const safe = String(status || "").toLowerCase();

  if (safe === "paid") return "border-green-400/30 bg-green-500/10 text-green-300";
  if (safe === "approved") return "border-blue-400/30 bg-blue-500/10 text-blue-300";
  if (safe === "rejected") return "border-red-400/30 bg-red-500/10 text-red-300";
  if (safe.includes("pending") || safe === "unpaid") {
    return "border-amber-400/30 bg-amber-500/10 text-amber-300";
  }

  return "border-white/10 bg-white/5 text-gray-300";
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

export default function OperationalPOInvoiceChain({
  chain,
  currentInvoiceId,
  onOpenInvoice,
}) {
  const hasChain = chain?.has_chain;
  const po = chain?.purchase_order || null;
  const summary = chain?.summary || {};
  const invoices = Array.isArray(chain?.invoices) ? chain.invoices : [];
  const intelligence = chain?.intelligence || {};

  if (!chain) {
    return (
      <div className="rounded-2xl border border-white/10 bg-[#101010] p-5">
        <div className="flex items-center gap-2 text-gray-400">
          <Clock className="h-4 w-4" />
          Loading PO invoice chain...
        </div>
      </div>
    );
  }

  if (!hasChain) {
    return (
      <div className="rounded-2xl border border-amber-400/20 bg-[#101010] p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-1 h-5 w-5 text-amber-300" />
          <div>
            <h2 className="text-lg font-semibold">Purchase Order Invoice Chain</h2>
            <p className="mt-1 text-sm text-amber-200">
              {chain.reason || "This invoice is not linked to a purchase order."}
            </p>
            <p className="mt-2 text-sm text-gray-400">
              {intelligence.recommendation ||
                "Link this invoice to a PO to unlock full procurement chain intelligence."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-[#101010] p-5">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-blue-300" />
            <h2 className="text-lg font-semibold">Purchase Order Invoice Chain</h2>
          </div>

          <p className="mt-1 text-sm text-gray-400">
            Full invoice family generated under the same purchase order.
          </p>

          <div className="mt-3 text-sm text-gray-300">
            PO:{" "}
            <span className="font-mono text-amber-300">
              {po?.po_number || po?.po_id || "—"}
            </span>
            {" · "}
            Vendor: {po?.vendor_name || "Unknown vendor"}
            {" · "}
            Status: {po?.po_status || "—"}
          </div>
        </div>

        <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-gray-300">
          <Receipt className="h-4 w-4" />
          {summary.invoice_count || invoices.length} invoice(s)
        </span>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-white/10 bg-black/25 p-4">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <PackageCheck className="h-4 w-4" />
            PO Ordered Gross
          </div>
          <div className="mt-1 text-xl font-bold">
            {currency(po?.ordered_gross)}
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/25 p-4">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <PackageCheck className="h-4 w-4" />
            Received Gross
          </div>
          <div className="mt-1 text-xl font-bold text-blue-300">
            {currency(po?.received_gross)}
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/25 p-4">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Receipt className="h-4 w-4" />
            Total Invoiced
          </div>
          <div className="mt-1 text-xl font-bold">
            {currency(summary.total_invoiced)}
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/25 p-4">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <CreditCard className="h-4 w-4" />
            Outstanding
          </div>
          <div className="mt-1 text-xl font-bold text-amber-300">
            {currency(summary.outstanding)}
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-white/10 bg-black/25 p-4">
        <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-semibold">Invoice Chain</div>
            <div className="text-xs text-gray-400">
              Auto invoices: {summary.auto_invoice_count || 0} · Manual invoices:{" "}
              {summary.manual_invoice_count || 0}
            </div>
          </div>

          <div className="flex gap-2 flex-wrap text-xs">
            <span className="rounded-full border border-green-400/30 bg-green-500/10 px-2 py-1 text-green-300">
              Paid {summary.paid_count || 0}
            </span>
            <span className="rounded-full border border-blue-400/30 bg-blue-500/10 px-2 py-1 text-blue-300">
              Approved {summary.approved_count || 0}
            </span>
            <span className="rounded-full border border-amber-400/30 bg-amber-500/10 px-2 py-1 text-amber-300">
              Pending {summary.pending_count || 0}
            </span>
            <span className="rounded-full border border-red-400/30 bg-red-500/10 px-2 py-1 text-red-300">
              Rejected {summary.rejected_count || 0}
            </span>
          </div>
        </div>

        {invoices.length === 0 ? (
          <p className="text-sm text-yellow-300">
            No linked invoices found for this purchase order.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="min-w-full text-sm">
              <thead className="bg-neutral-800 text-gray-200">
                <tr>
                  <th className="p-3 text-left">Invoice</th>
                  <th className="p-3 text-right">Amount</th>
                  <th className="p-3 text-right">Paid</th>
                  <th className="p-3 text-right">Remaining</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Created</th>
                  <th className="p-3 text-right">Open</th>
                </tr>
              </thead>

              <tbody>
                {invoices.map((inv, idx) => {
                  const isCurrent =
                    Number(inv.invoice_id) === Number(currentInvoiceId) ||
                    inv.is_current_invoice;

                  return (
                    <tr
                      key={inv.invoice_id}
                      className={
                        isCurrent
                          ? "bg-blue-500/10"
                          : idx % 2 === 0
                            ? "bg-neutral-900"
                            : "bg-neutral-800"
                      }
                    >
                      <td className="p-3 border-b border-white/10">
                        <div className="font-mono text-amber-300">
                          {inv.invoice_number || `#${inv.invoice_id}`}
                        </div>
                        <div className="text-xs text-gray-400">
                          ID {inv.invoice_id}
                          {inv.is_auto_invoice ? " · AUTO" : " · MANUAL"}
                          {isCurrent ? " · CURRENT" : ""}
                        </div>
                      </td>

                      <td className="p-3 border-b border-white/10 text-right">
                        {currency(inv.amount_due)}
                      </td>

                      <td className="p-3 border-b border-white/10 text-right text-green-300">
                        {currency(inv.total_paid)}
                      </td>

                      <td className="p-3 border-b border-white/10 text-right text-amber-300">
                        {currency(inv.balance_due ?? inv.remaining)}
                      </td>

                      <td className="p-3 border-b border-white/10">
                        <span
                          className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${statusClass(
                            inv.status || inv.payment_status
                          )}`}
                        >
                          {String(inv.status || inv.payment_status || "unknown").toUpperCase()}
                        </span>
                      </td>

                      <td className="p-3 border-b border-white/10">
                        {formatDate(inv.uploaded_at)}
                      </td>

                      <td className="p-3 border-b border-white/10 text-right">
                        <button
                          type="button"
                          disabled={isCurrent}
                          onClick={() => onOpenInvoice?.(inv.invoice_id)}
                          className="inline-flex items-center gap-1 rounded-lg border border-white/15 px-3 py-1 text-xs font-semibold hover:bg-white/10 disabled:opacity-40"
                        >
                          Open
                          <ArrowRight className="h-3 w-3" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-5 rounded-xl border border-white/10 bg-black/25 p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="mt-1 h-5 w-5 text-green-300" />
          <div>
            <div className="font-semibold">
              {intelligence.title || "Operational Chain Intelligence"}
            </div>
            <p className="mt-1 text-sm text-gray-300">{intelligence.message}</p>
            <p className="mt-2 text-sm text-amber-300">
              {intelligence.recommendation}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}